import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import Select from "../components/ui/AppReactSelect";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { createRetailerWholesalerOptionGroup, fetchRetailerWholesalerOptions } from '../actions/retailerWholesalerOptionActions';
import { fetchRetailerWholesalers } from '../actions/retailerWholesalerSectionActions';
import { getUserFromLocalStorage } from '../utilities/user';
import AppSelect from "../components/ui/AppSelect";

const RetailerWholesalerOptionGroupForm = ({ show, handleClose }) => {
    const user = getUserFromLocalStorage();
    const dispatch = useDispatch();
    const history = useHistory();
    const options = useSelector(state => state.retailerWholesalerOptions.options);
    const retailersWholesalers = useSelector(state => state.retailerWholesalerSections.retailersWholesalers);
    const isLoading = useSelector(state => state.options.isLoading);

    const [optionGroupName, setOptionGroupName] = useState('');
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [minOptions, setMinOptions] = useState(0);
    const [maxOptions, setMaxOptions] = useState(0);
    const [selectedRetailerWholesalerId, setSelectedRetailerWholesalerId] = useState("");

    useEffect(() => {
        dispatch(fetchRetailerWholesalers(user?.result?._id));
    }, [dispatch]);

    useEffect(() => {
        if (show) {
            dispatch(fetchRetailerWholesalerOptions(user?.result?._id));
        }
    }, [show, dispatch]);

    const handleOptionGroupSubmit = async (e) => {
        e.preventDefault();
        const optionGroupData = {
            name: optionGroupName,
            retailerWholesalerProductId: selectedRetailerWholesalerId,
            options: selectedOptions.map(option => ({
                value: option.value,
                mandatory: minOptions > 0 // Set mandatory based on minOptions
            })),
            minOptions,
            maxOptions,
            owner: user.result._id,
        };

        try {
            await dispatch(createRetailerWholesalerOptionGroup(optionGroupData));
            toast.success("Option group created successfully!");
            handleClose();

            // Navigate based on the user role
            if (user.result.user_role === 'SUPERUSER') {
                history.push('/admin/rw-menu');
            } else {
                history.push('/admin/rw-product-create');
            }
        } catch (error) {
            toast.error("Failed to create option group: " + (error.response?.data?.message || error.message));
        }
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Create New Option Group</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleOptionGroupSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Option Group Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter option group name"
                            value={optionGroupName}
                            onChange={e => setOptionGroupName(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group controlId="retailerWholesalerSelectOptionGroup">
                        <Form.Label>Select Retailer/Wholesaler</Form.Label>
                        <AppSelect block
                            value={selectedRetailerWholesalerId}
                            onChange={e => setSelectedRetailerWholesalerId(e.target.value)}
                        >
                            <option value="" disabled>Select a retailer/wholesaler</option>
                            {retailersWholesalers.map((retailerWholesaler) => (
                                <option key={retailerWholesaler._id} value={retailerWholesaler._id}>
                                    {retailerWholesaler.name}
                                </option>
                            ))}
                        </AppSelect>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Options</Form.Label>
                        <Select
                            isMulti
                            options={options.map(option => ({
                                value: option._id,
                                label: option.name,
                            }))}
                            value={selectedOptions}
                            onChange={setSelectedOptions}
                            placeholder="Select options..."
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Min Options</Form.Label>
                        <Form.Control
                            type="number"
                            placeholder="Enter minimum options"
                            value={minOptions}
                            onChange={e => setMinOptions(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Max Options</Form.Label>
                        <Form.Control
                            type="number"
                            placeholder="Enter maximum options"
                            value={maxOptions}
                            onChange={e => setMaxOptions(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                        style={{ color: isLoading ? 'gray' : 'white' }}
                    >

                        Cancel

                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        className="ms-2"
                        disabled={isLoading}
                        style={{ color: isLoading ? 'gray' : 'white' }}
                    >
                        {isLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                    style={{ color: 'gray' }}
                                />
                                Creating Option Group...
                            </>
                        ) : (
                            'Create Option Group'
                        )}
                    </Button>

                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default RetailerWholesalerOptionGroupForm;
